import { type ReactNode, useRef } from 'react';
import useOverflowedElements from '../../../hooks/useOverflowedElements';
import Divider from './Divider';
import InnerTab from './InnerTab';
import MoreInnerTabsDropdown from './MoreInnerTabsDropdown';
import { cssProperties } from './innerTabsTheme';
import type { AppInnerTabsItem } from './types';

interface AppInnerTabsProps {
  items: AppInnerTabsItem[];
  activeTabId: number;
  onTabClick: (tabId: number) => void;
  onTabClose: (tabId: number) => void;
  moreTabsDropdownIcon?: ReactNode;
}

const AppInnerTabs = ({
  items,
  activeTabId,
  onTabClick,
  onTabClose,
  moreTabsDropdownIcon,
}: AppInnerTabsProps) => {
  const tabsWrapperRef = useRef<HTMLUListElement>(null);

  const { hasOverflowedElements } = useOverflowedElements(tabsWrapperRef, [
    items.length,
    activeTabId,
  ]);

  return (
    <div
      className="flex flex-col w-full min-w-0 !bg-[(var(--ds-inner-tab-inactive-bg))]"
      style={cssProperties}
    >
      <div className="flex relative select-none h-[var(--ds-inner-tab-sizing-height)]">
        <div className="flex flex-shrink min-w-0 shrink basis-auto relative pt-1 me-auto">
          <Divider />
          <ul
            className="flex flex-shrink min-w-0 shrink basis-auto overflow-hidden gap-px relative px-1"
            role="tablist"
            ref={tabsWrapperRef}
          >
            {items.map((item) => {
              return (
                <li
                  className="flex min-w-[78px]"
                  key={item.id}
                >
                  <InnerTab
                    tab={item}
                    activeTabId={activeTabId}
                    onClick={onTabClick}
                    onClose={onTabClose}
                  />
                </li>
              );
            })}
          </ul>

          {hasOverflowedElements && (
            <div className="absolute end-0 inset-y-0 w-6 bg-gradient-to-l rtl:bg-gradient-to-r from-[var(--ds-inner-tab-inactive-bg)] pointer-events-none" />
          )}
        </div>

        <div className="flex justify-center w-[40px] shrink-0">
          {hasOverflowedElements && (
            <MoreInnerTabsDropdown
              items={items}
              activeTabId={activeTabId}
              onTabClick={onTabClick}
              onTabClose={onTabClose}
              dropdownIcon={moreTabsDropdownIcon}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AppInnerTabs;
